import {
  AddonPriceItem,
  AgeGroups,
  BrandType,
  CampaignAddon,
  CountryCode,
  CreatorQuality,
  Currency,
  Industry,
  ModelType,
  Platform,
  ProductType,
  VideoDuration,
  VideoType,
  VoiceType,
  AspectRatio,
  CreatorPetsOwner,
  ChildAgeRange,
  CampaignVisibility,
} from '../gql/types'
import moment from 'moment-timezone'
import { Icon, IconType } from '../components'
import {
  PricingData,
  formatPriceInCents,
  formattedVideoPrice,
} from './PricingUtil'

export const mapCountryCodeToFlagAndText = (countryCode: string) => {
  switch (countryCode) {
    case CountryCode.US:
      return '🇺🇸 United States'
    case CountryCode.GB:
      return '🇬🇧 United Kingdom'
    case CountryCode.AU:
      return '🇦🇺 Australia'
    default:
      return countryCode
  }
}

export const mapCountryCodeToFlag = (countryCode: string) => {
  switch (countryCode) {
    case CountryCode.US:
      return '🇺🇸'
    case CountryCode.GB:
      return '🇬🇧'
    case CountryCode.AU:
      return '🇦🇺'
    default:
      return countryCode
  }
}

export const mapFlagAndTextToCountryCode = (flagAndtext: string) => {
  switch (flagAndtext) {
    case '🇺🇸 United States':
      return CountryCode.US
    case '🇬🇧 United Kingdom':
      return CountryCode.GB
    case '🇦🇺 Australia':
      return CountryCode.AU
    default:
      return flagAndtext
  }
}

export const countryOptions = [
  '🇺🇸 United States',
  '🇬🇧 United Kingdom',
  '🇦🇺 Australia',
]

export const mapCurrencyToSign = (currency: Currency) => {
  switch (currency) {
    case Currency.USD:
      return '$'
    case Currency.GBP:
      return '£'
    case Currency.EUR:
      return '€'
    case Currency.AUD:
      return 'A$'
    default:
      return currency
  }
}

export const getAgeGroupByBirthday = (birthday: Date): AgeGroups => {
  const age = moment().diff(moment(birthday), 'years')
  if (age <= 24) {
    return AgeGroups.ageGroup1824
  }

  if (age >= 25 && age <= 34) {
    return AgeGroups.ageGroup2534
  }

  if (age >= 35 && age <= 44) {
    return AgeGroups.ageGroup3544
  }

  if (age > 44) {
    return AgeGroups.ageGroup45plus
  }
}

export const ageGroupsOptions = [
  {
    name: '18-24',
    type: AgeGroups.ageGroup1824,
    description: '',
  },
  {
    name: '25-34',
    type: AgeGroups.ageGroup2534,
    description: '',
  },
  {
    name: '35-44',
    type: AgeGroups.ageGroup3544,
    description: '',
  },
  {
    name: '45+',
    type: AgeGroups.ageGroup45plus,
    description: '',
  },
]

export const videoTypeOptions = [
  {
    name: 'Up to the creator',
    description: 'Our creators will send you their most creative takes',
    type: VideoType.upToTheCreator,
    icon: (
      <Icon
        type={IconType.sparkles}
        className='w-8 h-8 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
  {
    name: 'Testimonial',
    description:
      'Honest statement about your product from a customer’s perspective',
    type: VideoType.testimonial,
    icon: (
      <Icon
        type={IconType.megaphone}
        className='w-8 h-8 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
  {
    name: 'Unboxing',
    description:
      'Taking your product out of its original box and doing a short review',
    type: VideoType.unboxing,
    icon: (
      <Icon
        type={IconType.gift}
        className='w-8 h-8 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
  {
    name: 'How to',
    description:
      'Creators will record themselves explaining how your product works',
    type: VideoType.howTo,
    icon: (
      <Icon
        type={IconType.academic}
        className='w-8 h-8 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
]

export const productTypeOptions = [
  {
    name: 'Physical',
    description: 'Delivery required',
    type: ProductType.physical,
    icon: (
      <Icon
        type={IconType.cube}
        className='w-8 h-8 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
  {
    name: 'Digital',
    description: 'Delivery not required',
    type: ProductType.digital,
    icon: (
      <Icon
        type={IconType.computer}
        className='w-8 h-8 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
]

export const modelTypeOptions = [
  {
    name: 'Body & Face',
    description: 'Creators will film themselves using or showing your product',
    type: ModelType.bodyFace,
    icon: (
      <Icon
        type={IconType.framePerson}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
  {
    name: 'Hands only',
    description: 'Close-up shot of the product in someone’s hands',
    type: ModelType.handsOnly,
    icon: (
      <Icon
        type={IconType.handRaised}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
]

export const voiceTypeOptions = [
  {
    // name: 'Voiceover / Talking to the camera',
    name: 'Voiceover',
    description: 'Creator will add a voiceover narration to your video.',
    type: VoiceType.overVoice,
    icon: (
      <Icon
        type={IconType.voiceOver}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
  {
    name: 'No Voice',
    description: 'Creator will produce your video without any voiceover.',
    type: VoiceType.noVoice,
    icon: (
      <Icon
        type={IconType.noVoice}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
]

export const platformTypeOptions = [
  {
    name: 'Instagram',
    description: 'Optimize video for the best engagement on Instagram',
    type: Platform.instagram,
    icon: (
      <Icon
        type={IconType.instagram}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
  {
    name: 'Tiktok',
    description: 'Optimize video for the best engagement on Tiktok',
    type: Platform.tiktok,
    icon: (
      <Icon
        type={IconType.tiktok}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
]

export const qualityOptions = (premiumPriceFormatted: string) => [
  {
    name: 'Regular creators',
    description: (
      <div>
        <p>
          Emerging creators launching their careers but with great video editing
          skills.
        </p>
        <p className='mt-4 font-bold'>INCLUDED</p>
      </div>
    ),
    type: CreatorQuality.regular,
    icon: (
      <Icon
        type={IconType.camera}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
  {
    name: 'Premium creators',
    description: (
      <div>
        <p>
          A curated collection of highly-rated, skilled creators with a lot of
          experience.
        </p>
        <p className='mt-4 font-bold'>
          + {premiumPriceFormatted} <sub>/ each video</sub>
        </p>
      </div>
    ),
    type: CreatorQuality.premium,
    icon: (
      <Icon
        type={IconType.premium}
        className='w-6 h-6 text-amber-400'
        aria-hidden='true'
      />
    ),
  },
]

export const brandTypeOptions = [
  {
    name: 'Brand',
    // description: 'Brand',
    type: BrandType.brand,
    icon: (
      <Icon
        type={IconType.store}
        className='w-4 h-4 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
  {
    name: 'Agency',
    // description: 'Agency',
    type: BrandType.agency,
    icon: (
      <Icon
        type={IconType.megaphone}
        className='w-4 h-4 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
]

export const campaignVisibilityOptions = [
  {
    name: 'Public',
    description: 'Open to all creators',
    type: CampaignVisibility.public,
    icon: (
      <Icon
        type={IconType.globe}
        className='w-4 h-4 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
  {
    name: 'Private',
    description: 'Invitation-only creators',
    type: CampaignVisibility.private,
    icon: (
      <Icon
        type={IconType.lock}
        className='w-4 h-4 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
]

export const videoDurationOptions = ({
  currency,
  pricing,
  quality,
}: {
  currency: Currency
  pricing: PricingData
  quality?: CreatorQuality
}) => [
  {
    name: '15-20 seconds',
    description: formattedVideoPrice({
      videoDuration: VideoDuration.quarter,
      currency,
      pricing,
      quality,
    }),
    type: VideoDuration.quarter,
    icon: (
      <Icon
        type={mapVideoDurationToIconType(VideoDuration.quarter)}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
    save: null,
  },
  {
    name: '20-40 seconds',
    description: formattedVideoPrice({
      videoDuration: VideoDuration.half,
      currency,
      pricing,
      quality,
    }),
    type: VideoDuration.half,
    icon: (
      <Icon
        type={mapVideoDurationToIconType(VideoDuration.half)}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
    save: '30%',
  },
  {
    name: '40-60 seconds',
    description: formattedVideoPrice({
      videoDuration: VideoDuration.minute,
      currency,
      pricing,
      quality,
    }),
    type: VideoDuration.minute,
    icon: (
      <Icon
        type={mapVideoDurationToIconType(VideoDuration.minute)}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
    save: '60%',
  },
]

// subtitles music add-ons options
export const addonsOptions = (addonsPrice?: any, currency?: Currency) => [
  {
    name: 'Captions',
    description: (
      <div>
        <p>The creator will add captions to the final version of the video.</p>
        <p className='font-bold mt-2'>
          +{' '}
          {formatPriceInCents({
            priceInCents: addonsPrice.find(
              (p) => p.addon === CampaignAddon.subtitles,
            ).priceCents,
            currency: currency,
          })}{' '}
          <sub>/ each video</sub>
        </p>
      </div>
    ),
    type: CampaignAddon.subtitles,
    icon: (
      <Icon
        type={IconType.subtitles}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
  {
    name: 'Captions & Music',
    description: (
      <div>
        <p>
          The creator will add both captions and music to the final version of
          the video.
        </p>
        <p className='font-bold mt-2'>
          +{' '}
          {formatPriceInCents({
            priceInCents: addonsPrice.find(
              (p) => p.addon === CampaignAddon.subtitlesMusic,
            ).priceCents,
            currency: currency,
          })}{' '}
          <sub>/ each video</sub>
        </p>
      </div>
    ),
    type: CampaignAddon.subtitlesMusic,
    icon: (
      <Icon
        type={IconType.music}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
]

export const secondEditsVersionAddonsOptions = (
  addonsPrice?: any,
  currency?: Currency,
) => [
  {
    name: 'Second edits version',
    description: (
      <div>
        <p>The creator will do edits on the final version of the video.</p>
        {/* <p className='font-bold mt-2'>
          +{' '}
          {formatPriceInCents({
            priceInCents: addonsPrice.find(
              (p) => p.addon === CampaignAddon.secondEditedVersion,
            ).priceCents,
            currency: currency,
          })}{' '}
          <sub>/ each video</sub>
        </p> */}
      </div>
    ),
    type: CampaignAddon.secondEditedVersion,
    icon: (
      <Icon
        type={IconType.addVideo}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
]

export const mapVideoDurationToSeconds = (
  videoDuration: VideoDuration,
): string => {
  switch (videoDuration) {
    case VideoDuration.quarter:
      return '15-20'
    case VideoDuration.half:
      return '20-40'
    case VideoDuration.minute:
      return '40-60'
    default:
      return videoDuration
  }
}

export const mapVideoDurationToIconType = (
  videoDuration: VideoDuration,
): IconType => {
  switch (videoDuration) {
    case VideoDuration.quarter:
      return IconType.clock4
    case VideoDuration.half:
      return IconType.clock8
    case VideoDuration.minute:
      return IconType.clock12
    default:
      return IconType.clock3
  }
}

export const mapAspectRatioToText = (aspectRatio: AspectRatio) => {
  switch (aspectRatio) {
    case AspectRatio.vertical:
      // return 'Vertical Format (9:16)'
      return 'Vertical (9:16)'
    case AspectRatio.square:
      // return 'Square Format (1:1)'
      return 'Square (1:1)'
    case AspectRatio.portrait:
      // return 'Portrait Format (4:5)'
      return 'Portrait (4:5)'
    case AspectRatio.landscape:
      // return 'Landscape Format (16:9)'
      return 'Landscape (16:9)'
    default:
      return aspectRatio
  }
}

export const mapAspectRatioToTag = (aspectRatio: AspectRatio) => {
  switch (aspectRatio) {
    case AspectRatio.vertical:
      return '9:16'
    case AspectRatio.square:
      return '1:1'
    case AspectRatio.portrait:
      return '4:5'
    case AspectRatio.landscape:
      return '16:9'
    default:
      return aspectRatio
  }
}

export const mapAspectRatioToLabel = (aspectRatio: AspectRatio) => {
  switch (aspectRatio) {
    case AspectRatio.vertical:
      return 'Vertical'
    case AspectRatio.square:
      return 'Square'
    case AspectRatio.portrait:
      return 'Portrait'
    case AspectRatio.landscape:
      return 'Landscape'
    default:
      return aspectRatio
  }
}

export const mapCampaignAddonsTypeToText = (addon: CampaignAddon) => {
  switch (addon) {
    case CampaignAddon.subtitles:
      return 'Captions - an extra video file that includes captions'
    case CampaignAddon.subtitlesMusic:
      return 'Captions & Music - an extra video file that includes captions and music'
    case CampaignAddon.secondEditedVersion:
      return 'Second Edit Version - minor changes and fixes'
    case CampaignAddon.vertical:
      return 'Vertical Video Format (9:16)'
    case CampaignAddon.square:
      return 'Square Video Format (1:1)'
    case CampaignAddon.portrait:
      return 'Portrait Video Format (4:5)'
    default:
      return addon
  }
}

export const mapTextTypeToCampaignAddon = (text: string) => {
  switch (text) {
    case 'Captions - an extra video file that includes captions':
      return CampaignAddon.subtitles
    case 'Captions & Music - an extra video file that includes captions and music':
      return CampaignAddon.subtitlesMusic
    case 'Second Edit Version - minor changes and fixes':
      return CampaignAddon.secondEditedVersion
    case 'Vertical Video Format (9:16)':
      return CampaignAddon.vertical
    case 'Square Video Format (1:1)':
      return CampaignAddon.square
    case 'Portrait Video Format (4:5)':
      return CampaignAddon.portrait
    default:
      return text
  }
}

export const mapCampaignAddonsTypeToTextSimple = (addon: CampaignAddon) => {
  switch (addon) {
    case CampaignAddon.subtitles:
      return 'Captions'
    case CampaignAddon.subtitlesMusic:
      return 'Captions & Music'
    case CampaignAddon.secondEditedVersion:
      return 'Second Edit Version'
    case CampaignAddon.vertical:
      return 'Vertical Format (9:16)'
    case CampaignAddon.square:
      return 'Square Format (1:1)'
    case CampaignAddon.portrait:
      return 'Portrait Format (4:5)'
    default:
      return addon
  }
}

/**
 * Brand Addons
 */

export const mapAddonsToTooltipTextForBrand = (addons: CampaignAddon[]) => {
  if (addons.length === 0) {
    return `Purchase add-ons to get altered versions of the original video. These can be captions, music, or a second edit version.`
  }

  let tooltipText = `You have purchased ${addons.length} add-on${addons.length > 1 ? 's' : ''}: ${addons.map((addon) => mapCampaignAddonsTypeToTextSimple(addon)).join(', ')}. `
  return tooltipText
}

/**
 * Creator Addons
 */
export const mapAddonsToTooltipTextForCreators = (addons: CampaignAddon[]) => {
  if (addons.length === 0) {
    return null
  }

  const s = `${addons.length > 1 ? 's' : ''}`
  const tooltipText = `You have to provide ${addons.length} additional video${s} on this campaign: ${addons.map((addon) => mapCampaignAddonsTypeToTextSimple(addon)).join(', ')}. Make sure to provide both the original video and the altered version${s}.`

  return tooltipText
}

export const mapIndustryToText = (industry: Industry | string) => {
  switch (industry) {
    case Industry.fashion:
      return 'Fashion & Apparel'
    case Industry.homeware:
      return 'Home Goods'
    case Industry.wellness:
      return 'Wellness'
    case Industry.health:
      return 'Health'
    case Industry.skincare:
      return 'Skincare'
    case Industry.jewellery:
      return 'Jewellery & Extras'
    case Industry.food:
      return 'Food & Beverages'
    case Industry.beauty:
      return 'Beauty & Care'
    case Industry.fitness:
      return 'Sports & Fitness'
    case Industry.pet:
      return 'Pets'
    case Industry.hair:
      return 'Hair'
    case Industry.makeup:
      return 'Makeup'
    case Industry.outdoors:
      return 'Outdoors'
    case Industry.essentials:
      return 'Essentials'
    case Industry.software:
      return 'Software'
    case Industry.baby:
      return 'Baby'
    case Industry.toys:
      return 'Toys'
    case Industry.services:
      return 'Services'
    case Industry.gaming:
      return 'Gaming'
    case Industry.fintech:
      return 'Fintech'
    case Industry.consumerGoods:
      return 'Consumer Goods'
    case Industry.kidsAndFamily:
      return 'Kids & Family'
    case Industry.electronics:
      return 'Electronics'
    case Industry.other:
      return 'Other'
    default:
      return null
  }
}

export const aspectRatioOptions = [
  {
    name: 'Vertical (9:16)',
    description: null,
    type: AspectRatio.vertical,
    icon: (
      <Icon
        type={IconType.vertical}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
  {
    name: 'Square (1:1)',
    description: null,
    type: AspectRatio.square,
    icon: (
      <Icon
        type={IconType.square}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
  {
    name: 'Portrait (4:5)',
    description: null,
    type: AspectRatio.portrait,
    icon: (
      <Icon
        type={IconType.portrait}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
  {
    name: 'Landscape (16:9)',
    description: null,
    type: AspectRatio.landscape,
    icon: (
      <Icon
        type={IconType.landscape}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
]

export const videoFormatAddonOptions = (
  addonsPrice?: any,
  currency?: Currency,
) => [
  {
    name: 'Vertical (9:16)',
    description: (
      <div>
        {/* <p>For ex. IG stories</p> */}
        <p className='font-bold mt-2'>
          +{' '}
          {formatPriceInCents({
            priceInCents: addonsPrice.find(
              (p) => p.addon === CampaignAddon.vertical,
            ).priceCents,
            currency: currency,
          })}{' '}
          <sub>/ each video</sub>
        </p>
      </div>
    ),
    type: CampaignAddon.vertical,
    icon: (
      <Icon
        type={IconType.vertical}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
  {
    name: 'Square (1:1)',
    description: (
      <div>
        {/* <p>For ex. IG feed</p> */}
        <p className='font-bold mt-2'>
          +{' '}
          {formatPriceInCents({
            priceInCents: addonsPrice.find(
              (p) => p.addon === CampaignAddon.square,
            ).priceCents,
            currency: currency,
          })}{' '}
          <sub>/ each video</sub>
        </p>
      </div>
    ),
    type: CampaignAddon.square,
    icon: (
      <Icon
        type={IconType.square}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
  {
    name: 'Portrait (4:5)',
    description: (
      <div>
        {/* <p>For ex. IG posts</p> */}
        <p className='font-bold mt-2'>
          +{' '}
          {formatPriceInCents({
            priceInCents: addonsPrice.find(
              (p) => p.addon === CampaignAddon.portrait,
            ).priceCents,
            currency: currency,
          })}{' '}
          <sub>/ each video</sub>
        </p>
      </div>
    ),
    type: CampaignAddon.portrait,
    icon: (
      <Icon
        type={IconType.portrait}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
]

export const mapAddonToAspectRatio = (addon: CampaignAddon) => {
  switch (addon) {
    case CampaignAddon.vertical:
      return AspectRatio.vertical
    case CampaignAddon.square:
      return AspectRatio.square
    case CampaignAddon.portrait:
      return AspectRatio.portrait
    default:
      return addon
  }
}

export const mapAspectRatioToIcon = (aspectRatio: AspectRatio) => {
  switch (aspectRatio) {
    case AspectRatio.vertical:
      return IconType.vertical
    case AspectRatio.square:
      return IconType.square
    case AspectRatio.portrait:
      return IconType.portrait
    case AspectRatio.landscape:
      return IconType.landscape
    default:
      return aspectRatio
  }
}

export const mapPetToIcon = (pet: CreatorPetsOwner | string) => {
  switch (pet) {
    case CreatorPetsOwner.dog:
      return IconType.petsOwnerDog
    case CreatorPetsOwner.cat:
      return IconType.petsOwnerCat
    case CreatorPetsOwner.other:
      return IconType.petsOwnerOther
    case CreatorPetsOwner.noPets:
      return IconType.x
    default:
      return null
  }
}

export const mapAgeGroupToText = (ageGroup: AgeGroups) => {
  switch (ageGroup) {
    case AgeGroups.ageGroup1824:
      return '18-24 years'
    case AgeGroups.ageGroup2534:
      return '25-34 years'
    case AgeGroups.ageGroup3544:
      return '35-44 years'
    case AgeGroups.ageGroup45plus:
      return '45+ years'
    default:
      return ageGroup
  }
}

export const mapChildToIcon = (childAgeRange: ChildAgeRange | string) => {
  switch (childAgeRange) {
    case ChildAgeRange.upTo2:
      return IconType.childBaby
    case ChildAgeRange.twoToSix:
      return IconType.childKid
    case ChildAgeRange.sixPlus:
      return IconType.childTeen
    case ChildAgeRange.noChildren:
      return IconType.x
    default:
      return null
  }
}

export const petsOwnerOptions = [
  {
    value: CreatorPetsOwner.noPets,
    label: 'No pets',
    icon: (
      <Icon
        type={IconType.x}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
  {
    value: CreatorPetsOwner.cat,
    label: 'Cat',
    icon: (
      <Icon
        type={IconType.petsOwnerCat}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
  {
    value: CreatorPetsOwner.dog,
    label: 'Dog',
    icon: (
      <Icon
        type={IconType.petsOwnerDog}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
  {
    value: CreatorPetsOwner.other,
    label: 'Other pet',
    icon: (
      <Icon
        type={IconType.petsOwnerOther}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
]

export const childAgeRangeOptions = [
  {
    value: ChildAgeRange.noChildren,
    label: 'No children',
    icon: (
      <Icon
        type={IconType.x}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
  {
    value: ChildAgeRange.upTo2,
    label: '0-2 years',
    icon: (
      <Icon
        type={IconType.childBaby}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
  {
    value: ChildAgeRange.twoToSix,
    label: '2-6 years',
    icon: (
      <Icon
        type={IconType.childKid}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
  {
    value: ChildAgeRange.sixPlus,
    label: '6+ years',
    icon: (
      <Icon
        type={IconType.childTeen}
        className='w-6 h-6 text-gray-600'
        aria-hidden='true'
      />
    ),
  },
]
